import { useEffect, useRef, useState, Dispatch, MutableRefObject } from 'react';
import getUuidByString from 'uuid-by-string';
import {
  Card,
  ChargeVerifyBuyerDetails,
  StoreVerifyBuyerDetails,
  Payments,
  PaymentRequestOptions,
  ApplePay,
  CashAppPay,
  GooglePay,
  CashAppPayButtonOptions,
} from '@square/web-payments-sdk-types';

// TODO: Migrate utilities to TypeScript
import { localizedShopPath, getLocaleString } from 'utilities/locales';
import { PAY_WITH_CASH, isFeatureEnabled } from 'utilities/features';

import { useCheckoutFlow } from 'typescript/context/checkout_flow';
import { useLocaleInfo } from 'typescript/context/locale_info';
import { CountryCode } from 'typescript/constants/localization';
import { CardIntent } from 'typescript/constants/enums/card';
import { Env, getEnvironment } from 'typescript/utilities/environment';
import { TOrderSummary, OrderAddress } from 'typescript/types/api/solidshop';
import { isCompleteOrderAddress } from 'typescript/utilities/address_utils';

const paymentFormDivId = 'empty-div-for-payment-form';
const hiddenGooglePayTestDivId = 'hidden-div-for-gpay-test';
const cashAppPayDivId = 'pay-with-cash-app';

const createPaymentRequestPayload = (
  orderData: TOrderSummary,
  storeCode: CountryCode,
): PaymentRequestOptions => ({
  requestBillingContact: true,
  currencyCode: orderData.payment_due.currency,
  countryCode: storeCode,
  total: {
    label: 'SQUARE SHOP',
    amount: orderData.payment_due.amount,
    pending: false,
  },
  lineItems: [
    {
      label: 'Subtotal',
      amount: orderData.payment_due.amount,
      pending: false,
    },
  ],
});

// Billing Address is not guaranteed to be complete as for alternate payment methods
// billing address comes from Web Payment SDK. In such cases, fall back to the
// shipping address or pickup location info for BOPIS.
const createVerificationDetails = (
  orderData: TOrderSummary,
  cardIntent: CardIntent,
  billingAddress?: OrderAddress,
): ChargeVerifyBuyerDetails | StoreVerifyBuyerDetails => {
  const addressDetails = isCompleteOrderAddress(billingAddress)
    ? {
        ...billingAddress,
        country_code: orderData.bopis_fulfillment_selected
          ? orderData.pickup_location_info.country_code
          : orderData.shipping_address.country_code,
      }
    : orderData.bopis_fulfillment_selected
      ? {
          ...orderData.pickup_location_info,
          locality: orderData.pickup_location_info?.city,
          administrative_district_level_1:
            orderData.pickup_location_info?.state,
        }
      : orderData.shipping_address;

  const contactDetails = orderData.bopis_fulfillment_selected
    ? orderData.pickup_person_info
    : orderData.contact_information;

  const combinedDetails = {
    ...contactDetails,
    ...addressDetails,
  };

  const commonDetails = {
    squareProduct: 'MARKET',
    billingContact: {
      familyName: combinedDetails.last_name,
      givenName: combinedDetails.first_name,
      email: combinedDetails.email,
      countryCode: combinedDetails.country_code,
      city: combinedDetails.locality,
      addressLines: [
        combinedDetails.address_line_1,
        combinedDetails.address_line_2 || '',
      ],
      postalCode: combinedDetails.postal_code,
      phone: combinedDetails.phone,
      state: combinedDetails.administrative_district_level_1,
    },
    ...([CardIntent.CHARGE, CardIntent.CHARGE_AND_STORE].includes(cardIntent)
      ? {
          amount: orderData.payment_due.amount,
          currencyCode: orderData.payment_due.currency,
        }
      : {}),
  };
  if (cardIntent === CardIntent.STORE) {
    return {
      ...commonDetails,
      intent: 'STORE',
    } as StoreVerifyBuyerDetails;
  } else {
    return {
      ...commonDetails,
      intent: cardIntent,
    } as ChargeVerifyBuyerDetails;
  }
};

const initGooglePay = async (
  payments: Payments,
  paymentRequest: PaymentRequestOptions,
): Promise<GooglePay> | null => {
  try {
    const googlePay = await payments.googlePay(
      payments.paymentRequest(paymentRequest),
    );
    await googlePay.attach(`#${hiddenGooglePayTestDivId}`);
    return googlePay;
  } catch {
    // if it fails it just means Google Pay is not supported
    return null;
  }
};

const initApplePay = async (
  payments: Payments,
  paymentRequest: PaymentRequestOptions,
): Promise<ApplePay> | null => {
  try {
    return await payments.applePay(payments.paymentRequest(paymentRequest));
  } catch {
    // if it fails it just means Apple Pay is not supported
    return null;
  }
};

interface CashAppPayParams {
  redirectURL: string;
  referenceId: string;
}

const cashAppPayParams = (
  redirectPath: string,
  referenceId: string,
): CashAppPayParams => {
  const redirectURL = `${window.location.origin}${redirectPath}`;

  return {
    redirectURL,
    referenceId,
  };
};

const initCashAppPay = async (
  payments: Payments,
  paymentRequest: PaymentRequestOptions,
  params: CashAppPayParams,
): Promise<CashAppPay> | null => {
  try {
    return await payments.cashAppPay(
      payments.paymentRequest(paymentRequest),
      params,
    );
  } catch {
    return null;
  }
};

const attachCashAppPay = (cashAppPay: CashAppPay): void => {
  const options: CashAppPayButtonOptions = {
    shape: 'semiround',
    width: 'full',
  };

  const cashAppDiv = document.getElementById(cashAppPayDivId);
  cashAppDiv.innerHTML = ''; // Clear existing content
  cashAppPay.attach(`#${cashAppPayDivId}`, options);
};

// TODO: Investigate if mounting payment form can be done in a more performant way.
const usePaymentSdk = (): boolean => {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  useEffect(() => {
    const script = document.createElement('script');

    script.src =
      getEnvironment() === Env.PRODUCTION
        ? 'https://web.squarecdn.com/v1/square.js'
        : 'https://staging.web.squarecdn.com/v1/square.js';
    script.async = true;
    script.onload = () => setScriptLoaded(true);

    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
      setScriptLoaded(false);
    };
  }, []);

  return scriptLoaded;
};

interface PaymentForm {
  sqPayments: MutableRefObject<Payments>;
  sqCard: Card;
  googlePay: GooglePay;
  applePay: ApplePay;
  cashAppPay: CashAppPay;
}

const usePaymentForm = (
  scriptLoaded: boolean,
  orderData: TOrderSummary,
): PaymentForm => {
  const { orderNumber, applicationId, locationId } = useCheckoutFlow();
  const { countryCode } = useLocaleInfo();

  const sqPayments = useRef<Payments | null>(null);

  // Payment Methods
  const [sqCard, setSqCard] = useState<any>(null);
  const [googlePay, setGooglePay] = useState<any>(null);
  const [applePay, setApplePay] = useState<any>(null);
  const [cashAppPay, setCashAppPay] = useState<any>(null);

  useEffect(() => {
    if (!scriptLoaded || !orderData || Object.entries(orderData).length === 0) {
      return;
    }

    const payments = (window as any).Square.payments(
      applicationId,
      locationId,
    ) as Payments;

    sqPayments.current = payments;
    payments.setLocale(getLocaleString());

    const cardPromise = payments.card();
    let applePayPromise, cashAppPayPromise, googlePayPromise;

    if (
      !orderData.upfront_card_payment_only &&
      !orderData.first_party_payment_only &&
      orderData.payment_due.payment_schedule !== 'installment'
    ) {
      const paymentRequest = createPaymentRequestPayload(
        orderData,
        countryCode,
      );
      googlePayPromise = initGooglePay(payments, paymentRequest);
      applePayPromise = initApplePay(payments, paymentRequest);

      if (isFeatureEnabled(PAY_WITH_CASH)) {
        const cashAppPaymentRequest = createPaymentRequestPayload(
          orderData,
          countryCode,
        );

        const params = cashAppPayParams(
          `${localizedShopPath()}/checkout/v3/cash_app_pay`,
          getUuidByString(orderNumber + orderData.total.amount),
        );

        cashAppPayPromise = initCashAppPay(
          payments,
          cashAppPaymentRequest,
          params,
        );
      }
    }

    cardPromise.then((result: any) => {
      setSqCard(result);
      const paymentFormDiv = document.getElementById(paymentFormDivId);
      if (paymentFormDiv) {
        paymentFormDiv.innerHTML = ''; // Clear existing content
        result.attach(`#${paymentFormDivId}`);
      }
    });

    const addDigitalWallet = (setter: Dispatch<any>) => (wallet: any) => {
      if (wallet) setter(wallet);
    };
    googlePayPromise?.then(addDigitalWallet(setGooglePay));
    applePayPromise?.then(addDigitalWallet(setApplePay));
    cashAppPayPromise?.then(addDigitalWallet(setCashAppPay));
  }, [scriptLoaded, orderData]);

  return {
    sqPayments,
    sqCard,
    googlePay,
    applePay,
    cashAppPay,
  };
};

export {
  createPaymentRequestPayload,
  createVerificationDetails,
  initGooglePay,
  initApplePay,
  initCashAppPay,
  cashAppPayParams,
  attachCashAppPay,
  usePaymentSdk,
  usePaymentForm,
  paymentFormDivId,
  hiddenGooglePayTestDivId,
  cashAppPayDivId,
};
